import { Component, computed, inject, input, OnInit, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { FileDocument } from '@lib/models/file-document.model';
import {
  EtatDer,
  FieldToComplete,
  FileMetadata,
  GedDocumentType,
  SignatureState,
  TypeMissingFieldEnum,
} from '@lib/models/generated/graphql';
import { LoadingService } from '@lib/services/loading.service';
import { OuiBannerAction } from 'omnium-ui/banner';
import { OuiDocumentViewer } from 'omnium-ui/document-viewer';
import { MenuNode } from 'omnium-ui/shared';
import { SHARED_IMPORTS } from 'projects/minotaure-lib/src/shared-imports';
import { filter, firstValueFrom, map } from 'rxjs';
import { CreateSignatureDialogComponent } from '@lib/components/signature/create-signature-dialog/create-signature-dialog.component';
import { ConnaissanceClientDocumentBase } from '../../components/connaissance-client-document-base';
import { DERStepService } from './der-step.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { getRawFileId } from '@lib/utils/app-utils';

@Component({
  selector: 'app-der-step',
  imports: [SHARED_IMPORTS, OuiDocumentViewer],
  templateUrl: './der-step.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './der-step.component.scss',
  host: { '[class.document-request-mode]': 'isDocumentRequestMode()' },
})
export class derStepComponent extends ConnaissanceClientDocumentBase implements OnInit {
  protected override readonly documentName = 'DER';
  public loadingService = inject(LoadingService);
  private derService = inject(DERStepService);

  readonly mode = input<'connaissanceClient' | 'documentRequest'>('connaissanceClient');
  readonly investisseurContext = input<boolean>(true);
  readonly validated = output<FileDocument>();
  readonly closed = output<void>();
  readonly isDocumentRequestMode = computed<boolean>(() => this.mode() === 'documentRequest');

  updateBannerAction: OuiBannerAction = {
    label: 'Aller sur MyProdémial',
    action: this.openMyProdemial.bind(this),
  };
  shmBannerAction: OuiBannerAction = {
    label: 'Contacter nos services',
    action: this.openSHM.bind(this),
  };

  private readonly champManquantSingulier: string = "Le champ {0} n'est actuellement pas renseigné";
  private readonly champManquantPluriel: string = 'Les champs {0} ne sont actuellement pas renseignés';

  readonly isLoading = signal<boolean>(true);
  readonly fileUrls = computed<string[]>(() => [this.unsignedDocument()?.url].filter(Boolean) as string[]);
  readonly derState = toSignal(this.recueilService.recueilState$.pipe(map(state => state?.derEtat)), {
    initialValue: EtatDer.ACompleter,
  });

  readonly fieldsToCompleteProdemial = signal<string | undefined>(undefined);
  readonly fieldsToCompleteSHM = signal<string | undefined>(undefined);
  readonly displayFieldsToComplete = computed(() => {
    const fieldsToCompleteProdemial = this.fieldsToCompleteProdemial();
    const fieldsToCompleteSHM = this.fieldsToCompleteSHM();
    return fieldsToCompleteProdemial != undefined || fieldsToCompleteSHM != undefined;
  });

  readonly canSendToSignature = computed<boolean>(() => this.derState() !== EtatDer.EnSignature);
  public downloadMenuItems: MenuNode[] = [
    {
      name: 'Télécharger le document non signé',
      action: () => this.downloadDocument(this.unsignedDocument()),
    },
    {
      name: 'Télécharger le document signé',
      action: () => this.downloadDocument(this.signedDocument()),
    },
  ];

  public readonly isGestionPrivee = computed(() => this.derService.isGestionPrivee());

  readonly canContinue = computed<boolean>(() => !this.isLoading() && !!this.unsignedDocument() && !this.displayFieldsToComplete());
  readonly continueHint = computed<string | null>(() =>
    this.displayFieldsToComplete() ? "Compléter l'information manquante pour continuer" : null
  );

  async ngOnInit(): Promise<void> {
    await this.fetchDER();
  }

  protected async fetchDER(forceUpdate: boolean = false) {
    this.fieldsToCompleteProdemial.set(undefined);
    this.fieldsToCompleteSHM.set(undefined);
    this.unsignedDocument.set(null);
    this.signedDocument.set(null);
    this.isLoading.set(true);
    // Force update
    if (forceUpdate) {
      const unsignedDocument = await this.derService.loadForceUnsignedDocument();
      if (unsignedDocument?.fileDocument) {
        this.unsignedDocument.set(unsignedDocument.fileDocument);
        this.investisseurService.refreshRecueilState();
      } else if (unsignedDocument?.fieldsToComplete) {
        this.setFieldsToUpdate(unsignedDocument.fieldsToComplete);
      }
      // DER to sign
    } else {
      const unsignedDocument = await this.derService.loadUnsignedDocument(this.investisseurContext());
      if (unsignedDocument?.fileDocument) {
        this.unsignedDocument.set(unsignedDocument.fileDocument);
      } else if (unsignedDocument?.fieldsToComplete) {
        this.setFieldsToUpdate(unsignedDocument.fieldsToComplete);
      }
    }
    // DER Signed or in progress
    if (!forceUpdate && !this.isDocumentRequestMode() && [EtatDer.EnSignature, EtatDer.Signe].includes(this.derState() as EtatDer)) {
      const documents = await firstValueFrom(
        this.investisseurService.investisseurDocuments$.pipe(filter((docs): docs is FileMetadata[] => docs !== null))
      );

      const derDocumentId = await firstValueFrom(this.recueilService.recueilState$.pipe(map(state => state?.deRdocumentID)));
      const signedFile = documents
        ?.filter(
          f =>
            f.typeDocumentCode === GedDocumentType.Der &&
            f.etatSignature === SignatureState.Completed &&
            getRawFileId(f.fileId) === getRawFileId(derDocumentId)
        )
        ?.sort(
          (a, b) => new Date(b.dateDeDerniereModification || 0).getTime() - new Date(a.dateDeDerniereModification || 0).getTime()
        )?.[0];
      if (signedFile) {
        this.signedDocument.set(FileDocument.fromFileMetadata(signedFile, 'Document entrée en relation'));
      }
    }
    this.isLoading.set(false);
  }

  async forceDERUpdate() {
    await this.fetchDER(this.investisseurContext());
  }

  continueWithDer(): void {
    const der = this.unsignedDocument();
    if (der && this.canContinue()) {
      this.validated.emit(der);
    }
  }

  format(template: string, ...args: any[]): string {
    return template.replace(/{(\d+)}/g, (match, index) => (typeof args[index] !== 'undefined' ? args[index] : match));
  }

  private setFieldsToUpdate(fieldsToComplete: FieldToComplete[]) {
    const prodemialFields = fieldsToComplete.filter(f => f.type === TypeMissingFieldEnum.Prodemial);
    const shmFields = fieldsToComplete.filter(f => f.type === TypeMissingFieldEnum.Shm);
    // PRODEMIAL
    if (prodemialFields.length > 0) {
      const isPlural = prodemialFields.length > 1;
      const fieldList = prodemialFields.map(f => f.message).join(', ');
      const message = this.format(isPlural ? this.champManquantPluriel : this.champManquantSingulier, fieldList);
      this.fieldsToCompleteProdemial.set(message);
    }

    // SHM
    if (shmFields.length > 0) {
      const isPlural = shmFields.length > 1;
      const fieldList = shmFields.map(f => f.message).join(', ');
      const message = this.format(isPlural ? this.champManquantPluriel : this.champManquantSingulier, fieldList);
      this.fieldsToCompleteSHM.set(message);
    }
  }

  openMyProdemial() {
    if (this.authService.isBackOffice()) {
      return;
    }
    const prodemialUrl = this.environment.prodemialProfilUrl || 'https://my.prodemial.fr/profil';
    window.open(prodemialUrl, '_blank');
  }

  openSHM() {
    const zendeskUrl =
      this.environment.zendeskUrl || 'https://prodemial.zendesk.com/hc/fr/articles/360020632439-Contacter-les-services-internes';
    window.open(zendeskUrl, '_blank');
  }

  public sendToSignature(): void {
    const dialogRef = this.dialogService.openDialog(CreateSignatureDialogComponent, { documents: [this.unsignedDocument()] });
    dialogRef.afterClosed().subscribe(success => {
      if (success) {
        this.investisseurService.getInvestisseur(this.investisseurService.investisseurSig()?.code as string);
      }
    });
  }

  public async downloadDocument(fileDocument: FileDocument | null): Promise<void> {
    if (fileDocument) {
      await this.documentService.downloadDocumentFromUrl(fileDocument.url as string, fileDocument.fileName);
    }
  }
}
